import { AfterViewInit, Component, Inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import * as R from 'ramda'
import { combineLatest, from, of, Subject } from 'rxjs'
import { flatMap, map, mergeMap, startWith, switchMap, takeUntil, toArray } from 'rxjs/operators'
import {
  AccountGuard,
  ConfirmDialogService,
  DataCheckerService,
  isSomething,
  LocalStorage,
  Rental,
  SaveForm,
  selectAccount,
  selectAllRentals,
  TaxV3,
  taxV3ModalityLabels,
  updateRentalTaxes,
} from '@tokeet/tv3-platform'
import { NgSelectComponent } from '@ng-select/ng-select'
import { TaxModalities } from '@tokeet/cost-resolver'

export interface EditTaxesDialogConfig {
  rental: Rental
}

@Component({
  selector: 'app-edit-rental-taxes-dialog',
  templateUrl: './edit-rental-taxes-dialog.component.html',
  styleUrls: ['./edit-rental-taxes-dialog.component.scss'],
})
export class EditRentalTaxesDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('tax') ngSelects: QueryList<NgSelectComponent>

  form = this.fb.group({
    taxes: this.fb.array([this.createTax()]),
  })

  get taxes() {
    return this.form.get('taxes') as FormArray
  }

  currencySymbol: string
  taxV3Modalities = TaxModalities
  taxV3ModalityLabels = taxV3ModalityLabels

  rentals$ = this.store.pipe(
    select(selectAllRentals),
    map((rentals) => R.reject((r) => r.id === this.data.rental?.id, rentals))
  )
  rentalCtrl = new FormControl()

  availableTaxes: TaxV3[] = []
  destroy$ = new Subject<boolean>()

  constructor(
    public dialogRef: MatDialogRef<EditRentalTaxesDialogComponent>,
    private fb: FormBuilder,
    private storage: LocalStorage,
    private dataCheckerService: DataCheckerService,
    private confirmDialog: ConfirmDialogService,
    private store: Store<any>,
    @Inject(MAT_DIALOG_DATA) public data: EditTaxesDialogConfig
  ) {
    this.dataCheckerService.check([AccountGuard])
  }

  ngOnInit() {
    const taxes = isSomething(this.data.rental.taxes)
      ? this.data.rental.taxes
      : [
          {
            amount: undefined,
            type: 'percent',
            name: undefined,
            modality: TaxModalities.PerStay,
          },
        ]

    this.form = this.fb.group({
      taxes: this.fb.array(R.map((t: TaxV3) => this.createTax(t), taxes)),
    })

    this.currencySymbol = R.pathOr('$', ['currency', 'symbol'], this.data.rental)

    const user = this.storage.get('USER')

    combineLatest([this.store.pipe(select(selectAccount(user.account))), this.store.pipe(select(selectAllRentals))])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([account, rentals]) => {
        const accountTaxes = R.pathOr([], ['attributes', 'taxes'], account)
        const rentalTaxes = R.flatten(R.map((r) => r.taxes, rentals || []))

        this.availableTaxes = R.uniqBy(
          (t: TaxV3) => t.name,
          R.filter((t) => !!t, [...accountTaxes, ...rentalTaxes])
        )
      })
  }

  ngAfterViewInit() {
    this.ngSelects.changes.pipe(startWith(this.ngSelects)).subscribe((ngSelects) => {
      ngSelects.forEach((ngSelect) => {
        ngSelect['_handleTab'] = function () {
          if (this.isOpen) {
            if (this.itemsList.markedItem) {
              this.toggleItem(this.itemsList.markedItem)
            } else if (this.addTag) {
              this.selectTag()
            }
          }
        }
      })
    })
  }

  newTaxName(name) {
    const tax = new TaxV3()
    tax.name = name
    return tax
  }

  onAccountTaxSelected(tax: TaxV3, ctrl: FormGroup) {
    ctrl.patchValue(tax)
  }

  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  close() {
    this.dialogRef.close()
  }

  addTax(tax?: TaxV3) {
    this.taxes.push(this.createTax(tax))
  }

  removeTax(index: number) {
    this.taxes.removeAt(index)
  }

  onCopy(rental: Rental) {
    this.confirmDialog
      .confirm({
        title: $localize`Overwrite current taxes?`,
        body: $localize`Copying taxes from different rental will overwrite current taxes.`,
      })
      .subscribe(() => {
        const taxes = rental.taxes || []
        this.form = this.fb.group({
          taxes: this.fb.array(R.map((t: TaxV3) => this.createTax(t), taxes)),
        })
      })
  }

  @SaveForm()
  onSave(form: FormGroup) {
    if (form.invalid) {
      return
    }
    const taxes = form.getRawValue().taxes as TaxV3[]
    this.store.dispatch(updateRentalTaxes({ taxes, rental: this.data.rental }))
    this.close()
  }

  private createTax(tax?: TaxV3) {
    return this.fb.group({
      amount: [tax?.amount, [Validators.required]],
      name: [tax?.name, [Validators.required]],
      type: [tax?.type || 'percent'],
      tax_type: [tax?.tax_type, [Validators.required]],
      modality: [tax?.modality || TaxModalities.PerStay],
      // business_tax_id: [tax?.business_tax_id],
      // tot_registration_id: [tax?.tot_registration_id],
      // long_term_stay_exemption: [tax?.long_term_stay_exemption],
    })
  }
}
